/**
 * Kickoff — the startup meeting, inverted, as a branded paged flow.
 *
 * Flow (Guilherme, 19 Aug 2026): agents PRE-FILL from the Client Brain
 * before the call; the CM screen-shares this during the startup meeting and
 * walks the client through it page by page; the final Review page's
 * "Publish" hands the approved spec to Saga, who builds the campaign and
 * stops at the curation gate.
 *
 * The document itself is KickoffStudio: Lykke on the left (call, dictation,
 * text, files, chat), the whole scrollable document on the right, Submit
 * green only when every required field is in. Deep link: /agents/kickoff?k=<id>.
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Plus } from 'lucide-react';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { cn } from '@/components/ui/utils';
import { KickoffStudio } from './KickoffStudio';
import logoBeatly from '@/imports/1_(3).svg';

export function KickoffPage() {
  const location = useLocation();
  const selectedId = new URLSearchParams(location.search).get('k');
  return selectedId ? <KickoffStudio id={selectedId} /> : <KickoffList />;
}

// ── List + create ───────────────────────────────────────────────────────────

function KickoffList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [clientName, setClientName] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [brandId, setBrandId] = useState('');
  const [fromProposal, setFromProposal] = useState('');
  const { data: proposals } = useQuery({ queryKey: ['cm', 'kickoff-proposals'], queryFn: () => coworkApi.cm.kickoffs.proposals(), staleTime: 60_000, enabled: creating });

  const { data } = useQuery({
    queryKey: ['cm', 'kickoffs'],
    queryFn: () => coworkApi.cm.kickoffs.list(),
  });
  const { data: brands } = useQuery({
    queryKey: ['cowork', 'brands'],
    queryFn: () => coworkApi.brands.list(),
    staleTime: 5 * 60_000,
  });

  const create = useMutation({
    mutationFn: async () => {
      const r = await coworkApi.cm.kickoffs.create({
        clientName: clientName.trim() || undefined,
        campaignName: campaignName.trim() || undefined,
        brandId: brandId || undefined,
        fromPresentationId: fromProposal || undefined,
      });
      // Fill the document from the proposal before opening it — every value arrives quoted from the deck.
      if (fromProposal) { try { await coworkApi.cm.kickoffs.importProposal(r.kickoff.id, fromProposal); } catch { /* the studio can import again */ } }
      return r;
    },
    onSuccess: r => {
      void qc.invalidateQueries({ queryKey: ['cm', 'kickoffs'] });
      navigate(`/agents/kickoff?k=${r.kickoff.id}`);
    },
  });

  return (
    <div className="mx-auto max-w-4xl p-6">
      {/* Branded band */}
      <div className="relative mb-6 overflow-hidden rounded-[24px] bg-gradient-to-r from-[#2e024c] to-[#5a1a8a] p-6 shadow-lg">
        <div className="pointer-events-none absolute inset-0 opacity-25" style={{
          backgroundImage: 'radial-gradient(circle at 80% 20%, #be43ff 0%, transparent 50%), radial-gradient(circle at 20% 80%, #a9f600 0%, transparent 50%)',
        }} />
        <div className="relative flex items-center justify-between gap-4">
          <div>
            <img src={logoBeatly} alt="Beatly" className="mb-2 h-6 w-auto brightness-0 invert" />
            <h1 className="text-2xl text-white" style={{ fontWeight: 700 }}>Campaign kickoff</h1>
            <p className="mt-1 max-w-md text-[13px] text-white/70">
              Agents pre-fill it before the meeting. You review it live with your client. Publish builds the campaign.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreating(v => !v)}
            className="flex shrink-0 items-center gap-1.5 rounded-xl bg-[#a9f600] px-4 py-2.5 text-[13px] text-[#1a2e05] transition-transform hover:scale-[1.03]"
            style={{ fontWeight: 700 }}
          >
            <Plus className="h-4 w-4" aria-hidden /> New kickoff
          </button>
        </div>
      </div>

      {creating && (
        <div className="mb-5 rounded-2xl bg-white p-4 shadow-sm">
          {(proposals?.proposals.length ?? 0) > 0 && (
            <select value={fromProposal} onChange={e => { const id = e.target.value; setFromProposal(id); const p = proposals?.proposals.find(x => x.presentationId === id); if (p) { setClientName(p.clientName); setCampaignName(p.campaignName || p.title); setBrandId(p.brandId || ''); } }}
              className="mb-3 w-full rounded-xl border border-[#e9d5ff] bg-[#fcf7ff] px-3 py-2 text-[13px] outline-none focus:border-[#be43ff]">
              <option value="">Start from a proposal Lykke built (optional)…</option>
              {proposals!.proposals.map(p => <option key={p.presentationId} value={p.presentationId}>{p.title} — {[p.brandName, p.market, p.budget ? `${p.budget.toLocaleString('en')} ${p.currency || ''}` : '', p.complete ? 'complete' : `step ${p.step || 0}/6`].filter(Boolean).join(' · ')}</option>)}
            </select>
          )}
          <div className="grid gap-3 sm:grid-cols-3">
            <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Client name *"
              className="rounded-xl border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-[#be43ff]" />
            <input value={campaignName} onChange={e => setCampaignName(e.target.value)} placeholder="Campaign name (optional)"
              className="rounded-xl border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-[#be43ff]" />
            <select value={brandId} onChange={e => setBrandId(e.target.value)}
              className="rounded-xl border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-[#be43ff]">
              <option value="">Brand (links the Client Brain)…</option>
              {(brands?.brands ?? []).map(b => <option key={b.brandId} value={b.brandId}>{b.brandName?.trim() || 'Unnamed brand'}</option>)}
            </select>
          </div>
          <button
            type="button"
            disabled={!clientName.trim() || create.isPending}
            onClick={() => create.mutate()}
            className="mt-3 rounded-xl bg-[#be43ff] px-4 py-2 text-[13px] text-white transition-colors hover:bg-[#a832e6] disabled:opacity-50"
            style={{ fontWeight: 600 }}
          >
            {create.isPending ? (fromProposal ? 'Creating and importing the proposal…' : 'Creating…') : fromProposal ? 'Create from proposal & open' : 'Create & open'}
          </button>
        </div>
      )}

      <div className="space-y-2">
        {(data?.kickoffs ?? []).map(k => (
          <button
            key={k.id}
            type="button"
            onClick={() => navigate(`/agents/kickoff?k=${k.id}`)}
            className="flex w-full items-center gap-3 rounded-2xl bg-white px-4 py-3 text-left shadow-sm transition-all hover:shadow-md"
          >
            <ClipboardList className="h-4 w-4 shrink-0 text-[#be43ff]" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>
                {k.clientName}{k.campaignName && k.campaignName.trim().toLowerCase() !== k.clientName.trim().toLowerCase() ? ` — ${k.campaignName}` : ''}
              </span>
              <span className="text-[12px] text-[#98a2b3]">updated {new Date(k.updatedAt).toLocaleString()}</span>
            </span>
            <span className={cn('rounded-full px-2.5 py-0.5 text-[10.5px] font-bold',
              k.status === 'published' ? 'bg-[#12b76a]/10 text-[#12b76a]'
              : k.status === 'built' ? 'bg-[#2a5fb0]/10 text-[#2a5fb0]'
              : 'bg-[#f79009]/10 text-[#b54708]')}>
              {k.status === 'published' ? 'published' : k.status === 'built' ? 'draft built · pre-meeting' : 'draft'}
            </span>
          </button>
        ))}
        {data && data.kickoffs.length === 0 && !creating && (
          <p className="py-10 text-center text-[13px] text-[#98a2b3]">
            No kickoffs yet — create one before your next startup meeting.
          </p>
        )}
      </div>
    </div>
  );
}
